import cover_en from '@/assets/png/EF_CChildhood_Cover.png';

import { styled } from '@/styles/stitches.config';
import { Trans, t } from '@lingui/macro';
import Image from 'next/image';
import { DownloadReportButton } from './download-report-button';
import { SidebarLinks } from './sidebar-links';

const RapportCover = styled(Image, {
  width: 'initial',
  height: 'initial',
  maxWidth: '100%',
});

const Aside = styled('aside', {
  marginBlockEnd: '$6',

  '@bp4': {
    maxWidth: '378px',
  },
});

const ReportContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--colors-unicef)',
  color: 'white',
  borderRadius: '20px',
  paddingBlockEnd: '15px',
  border: '3px solid $unicef',
});

const Label = styled('p', {
  margin: 0,
  paddingBlock: '30px',
  textAlign: 'center',
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
});

const DownloadReportButtonContainer = styled('div', {
  paddingBlock: '30px',
  marginInline: 'auto',
});

const HorizontalLine = styled('div', {
  height: '1px',
  width: 'calc(100% + 6px)',
  marginInlineStart: '-3px',
  background: 'currentColor',
});

const SidebarLinksContainer = styled('div', {
  paddingInline: '10px',
  '@bp2': {
    paddingInline: '50px',
  },
});

export function Sidebar() {
  return (
    <Aside>
      <ReportContainer>
        <Label>
          <Trans id="about.latest-report">Latest report</Trans>
        </Label>

        <RapportCover
          placeholder="blur"
          src={cover_en}
          alt={t({
            id: 'about.report-cover-alt-text',
            message: 'Cover of the UNICEF report Changing Childhood',
          })}
          css={{ marginBlockEnd: '10px' }}
        />

        <DownloadReportButtonContainer>
          <DownloadReportButton
            singleTranslationUrl={'https://www.unicef.org/globalinsight/media/3311/file'}
          />
        </DownloadReportButtonContainer>

        <HorizontalLine />

        <Label
          css={{
            paddingBlockStart: '30px',
            paddingBlockEnd: '16px',
          }}
        >
          <Trans id="about.download-data-heading">Download Research Tools</Trans>
        </Label>

        <SidebarLinksContainer>
          <SidebarLinks
            links={[
              {
                href: '/downloads/unicef-changing-childhood-data-2022-2023.csv',
                children: <Trans id="about.csv-data">.CSV data</Trans>,
                download: t({
                  id: 'about.csv-download-filename',
                  message: 'unicef-changing-childhood-data.csv',
                }),
              },
              {
                href: 'https://www.unicef.org/globalinsight/media/3236/file',
                children: <Trans id="about.methodology-download">Methodology</Trans>,
                download: t({
                  id: 'about.methodology-download-filename',
                  message: 'unicef-changing-childhood-methodology.pdf',
                }),
              },
              {
                href: `https://www.unicef.org/globalinsight/media/3236/file`,
                children: <Trans id="about.codebook-download">Codebook</Trans>,
                download: t({
                  id: 'about.codebook-download-filename',
                  message: 'unicef-changing-childhood-codebook.docx',
                }),
              },
              {
                href: 'https://www.unicef.org/globalinsight/media/3241/file',
                children: <Trans id="about.questionnaire-download">Questionnaire</Trans>,
                download: t({
                  id: 'about.questionnaire-download-filename',
                  message: 'unicef-changing-childhood-questionnaire.pdf',
                }),
              },
            ]}
          />
        </SidebarLinksContainer>
      </ReportContainer>

      <ReportContainer css={{ background: 'white', color: '$unicef', marginBlockStart: '32px' }}>
        <Label>
          <Trans id="about.previous-report">Previous report</Trans>
        </Label>

        <DownloadReportButtonContainer css={{ paddingBlockStart: 0 }}>
          <DownloadReportButton colorVariant="light" />
        </DownloadReportButtonContainer>

        <HorizontalLine />

        <div>
          <Label
            css={{
              paddingBlockStart: '30px',
              paddingBlockEnd: '16px',
            }}
          >
            <Trans id="about.download-data-heading">Download Research Tools</Trans>
          </Label>

          <SidebarLinksContainer>
            <SidebarLinks
              links={[
                {
                  href: '/downloads/unicef-changing-childhood-data.csv',
                  children: <Trans id="about.csv-data">.CSV data</Trans>,
                  download: t({
                    id: 'about.csv-download-filename',
                    message: 'unicef-changing-childhood-data.csv',
                  }),
                },
                {
                  children: <Trans id="about.methodology-download">Methodology</Trans>,
                  download: t({
                    id: 'about.methodology-download-filename',
                    message: 'unicef-changing-childhood-methodology.pdf',
                  }),
                  options: [
                    {
                      label: 'English',
                      href: `https://www.unicef.org/globalinsight/media/2226/file`,
                    },
                    {
                      label: 'Español',
                      href: `https://www.unicef.org/globalinsight/media/2251/file`,
                    },
                    {
                      label: 'Français',
                      href: `https://www.unicef.org/globalinsight/media/2256/file`,
                    },
                    {
                      label: 'عربي',
                      href: `https://www.unicef.org/globalinsight/media/2261/file`,
                    },
                  ],
                },
                {
                  href: `https://www.unicef.org/globalinsight/media/2236/file`,
                  children: <Trans id="about.codebook-download">Codebook</Trans>,
                  download: t({
                    id: 'about.codebook-download-filename',
                    message: 'unicef-changing-childhood-codebook.docx',
                  }),
                },
                {
                  children: <Trans id="about.questionnaire-download">Questionnaire</Trans>,
                  download: t({
                    id: 'about.questionnaire-download-filename',
                    message: 'unicef-changing-childhood-questionnaire.pdf',
                  }),
                  options: [
                    {
                      label: 'English',
                      href: `https://www.unicef.org/globalinsight/media/2231/file`,
                    },
                    {
                      label: 'Español',
                      href: `https://www.unicef.org/globalinsight/media/2246/file`,
                    },
                    {
                      label: 'Français',
                      href: `https://www.unicef.org/globalinsight/media/2241/file`,
                    },
                    {
                      label: 'عربي',
                      href: `https://www.unicef.org/globalinsight/media/2276/file`,
                    },
                  ],
                },
              ]}
            />
          </SidebarLinksContainer>
        </div>
      </ReportContainer>
    </Aside>
  );
}
